<template>
  <li>
    <div
      class="_cart"
      @mouseenter="dropdownEvent($event)"
      @mouseleave="dropdownEvent($event)"
    >
      <a href="/checkout/cart">
        <span class="icon"></span>
        <span class="text hide-for-small-only">Winkelwagen</span>
        <span class="count" v-if="quoteItemsCount > 0">{{ quoteItemsCount }}</span>
      </a>
      <transition name="fade">
        <div
          v-if="cartMessage"
          key="toast"
          class="_toast _toast-error"
          role="alert"
        >
          <p>{{ cartMessage }}</p>
          <button
            type="button"
            class="_toast-close"
            @click.stop="clearCartMessage"
            :aria-label="$t('Close')"
          ></button>
        </div>
      </transition>
      <transition name="fade">
        <div class="_dropdown" v-show="showCartPopup && quoteItemsCount > 0">
          <div class="_added" v-show="showProductAdded">
            <p>{{ $t('Product has been added to cart') }}</p>
          </div>
          <div class="cart__items">
            <ul>
              <li v-for="item in quoteItems">
                <a :href="itemUrl(item)">
                  <strong>{{ parseInt(item.qty) }}x</strong>
                  <span class="_name">{{ item.name }}</span>
                  <span class="_price">{{ item.row_total_incl_tax.priceFormat() }}</span>
                </a>
              </li>
            </ul>
          </div>
          <div class="cart__totals" v-if="grandTotal">
            <ul class="_totals">
              <li>
                <span>Totaal</span>
                <span>{{ grandTotal.priceFormat() }}</span>
              </li>
            </ul>
            <ul class="_actions grid-x grid-padding-x">
              <li class="small-24 medium-14 cell"><a :href="continueShoppingUrl" @click="onContinueShopping" class="_button _secondary">{{ $t('Continue shopping') }}</a></li>
              <li class="small-24 medium-10 cell"><a href="/checkout/cart" class="_button _cta">{{ $t('To checkout') }}</a></li>
            </ul>
          </div>
        </div>
      </transition>
    </div>
  </li>
</template>

<script lang="ts">
  import {Component, Vue, Watch, Prop} from 'vue-property-decorator';

  @Component({
    name: 'header-cart'
  })

  export default class extends Vue {
    @Prop({default: 0}) readonly quoteItemCount: number;
    @Prop({default: false}) readonly quoteExists: boolean;

    private messageTimer: number = 0;

    private get cartTotals(): object {
      return this.$store.getters['CheckoutTotals/cartTotals'] || {};
    }

    private get quoteItems(): Array<object> {
      const items = this.$store.getters['CheckoutTotals/cartItems'];
      return Array.isArray(items) ? items : [];
    }

    private get quoteItemsCount(): number {
      return this.quoteItems.length > 0 ? this.quoteItems.length : this.quoteItemCount;
    }

    private get grandTotal(): number | null {
      return this.cartTotals['grand_total'] || null;
    }

    private get showCartPopup(): boolean {
      return this.$store.getters['CheckoutCart/showCartPopup'];
    }

    private get showProductAdded(): boolean {
      return this.$store.getters['CheckoutCart/showProductAdded'];
    }

    private get cartMessage(): string | null {
      return this.$store.getters['CheckoutCart/cartMessage'];
    }

    private get continueShoppingUrl(): string {
      try {
        const stored = sessionStorage.getItem('last-category-url');
        if (stored && stored.charAt(0) === '/' && stored.charAt(1) !== '/') {
          return stored;
        }
      } catch (e) {
        // sessionStorage may be unavailable
      }
      return '/';
    }

    private itemUrl(item: object): string {
      const urlKey = String(item['extension_attributes']?.['product_url_key'] || '').replace(/^\/+/, '');
      return urlKey ? `/${urlKey}` : '#';
    }

    private closeCartPopup(): void {
      this.$store.dispatch('CheckoutCart/showCartPopup', false);
    }

    private clearCartMessage(): void {
      window.clearTimeout(this.messageTimer);
      this.$store.dispatch('CheckoutCart/setCartMessage', null);
    }

    private onContinueShopping(e: Event): void {
      const target = this.continueShoppingUrl;
      const current = window.location.pathname + window.location.search;
      if (target === current || target === window.location.pathname) {
        e.preventDefault();
        this.closeCartPopup();
      }
    }

    private dropdownEvent(e): void {
      if (e.type === 'mouseenter' && this.quoteItemsCount === 0) {
        return;
      }
      this.$store.dispatch('CheckoutCart/showCartPopup', e.type === 'mouseenter');
    }

    @Watch('showCartPopup')
    closeShowProductAdded(newVal) {
      if (newVal) {
        const _this = this;
        setTimeout(function () {
          _this.$store.dispatch('CheckoutCart/showProductAdded', false);
        }, 4000);
      }
    }

    @Watch('cartMessage')
    onCartMessage(newVal) {
      window.clearTimeout(this.messageTimer);
      if (newVal) {
        this.messageTimer = window.setTimeout(() => {
          this.$store.dispatch('CheckoutCart/setCartMessage', null);
        }, 6000);
      }
    }

    private created(): void {
      if (this.quoteExists) {
        this.$store.dispatch('CheckoutTotals/fetchCartTotals');
      }

      window.addEventListener('CheckoutAddedProductToCartEvent', () => {
        this.$store.dispatch('CheckoutTotals/fetchCartTotals');
      });
    }

    private beforeDestroy(): void {
      window.clearTimeout(this.messageTimer);
    }
  }
</script>

<style scoped>
  .fade-enter-active, .fade-leave-active {
    transition: opacity .5s;
  }

  .fade-enter, .fade-leave-to {
    opacity: 0;
  }
</style>
